import {useContext} from "react";

import {Errors, KAError} from "@repo/logging";

import type {EmptyContentPath, ResolvedContentPath} from "../types/types.ts";

import {ContentPathContext} from "./content-path-provider.tsx";

export const EMPTY_CONTENT_PATH: EmptyContentPath = {
    domainId: null,
    courseId: null,
    unitId: null,
    lessonId: null,
    learnableNodeKind: null,
    learnableNodeId: null,
};

export const isEmptyContentPath = (
    contentPath: ResolvedContentPath,
): boolean => {
    return (
        contentPath.domainId === null &&
        contentPath.courseId === null &&
        contentPath.unitId === null &&
        contentPath.lessonId === null &&
        contentPath.learnableNodeKind === null &&
        contentPath.learnableNodeId === null
    );
};

type UseContentPathResult = {
    contentPath: ResolvedContentPath;
    previousContentPath: ResolvedContentPath | null | undefined;
};

/**
 * A hook that returns the current and previous content paths for the current
 * page. If no current contentPath is available, an empty content path is
 * returned.
 *
 * If the current page is not a content page or the content path cannot
 * be determined an error is thrown.
 */
const useContentPath = (): UseContentPathResult => {
    const result = useContext(ContentPathContext);
    if (!result) {
        throw new KAError(
            "useContentPath must be called within a ContentPathContextProvider",
            Errors.Internal,
        );
    }
    return {
        contentPath: result.contentPath ?? EMPTY_CONTENT_PATH,
        previousContentPath: result.previousContentPath,
    };
};

/**
 * A hook that returns the current and previous content paths for the current
 * page. If no current contentPath is available, an empty content path is
 * returned.
 *
 * If the current page is not a content page or the content path cannot
 * be determined null is returned.
 */
export const useContentPathIfAvailable = (): UseContentPathResult | null => {
    const result = useContext(ContentPathContext);

    if (!result) {
        return null;
    }

    return {
        contentPath: result.contentPath ?? EMPTY_CONTENT_PATH,
        previousContentPath: result.previousContentPath,
    };
};

export default useContentPath;
